import { Alert, Button, CircularProgress } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import { useEffect, useRef, useState } from "react";
import { CachedDocument, extractSubcollection } from "../../adapters/Firebase/Firebase";
import { useTranslation } from "../../contexts/TranslationContext";
import { createSessionsCsv, downloadSessionsCsv, isCsvSessionSupported } from "./sessionCsv";

interface Props {
  sessions: CachedDocument[];
  disabled?: boolean;
}

export default function ExportSessionsCsvButton({ sessions, disabled }: Props) {
  const { translate } = useTranslation();
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState(false);
  const active = useRef(false);
  const busy = useRef(false);
  useEffect(() => {
    active.current = true;
    return () => { active.current = false; };
  }, []);

  const supportedCount = sessions.filter(isCsvSessionSupported).length;

  async function exportCsv() {
    if (disabled || busy.current || !supportedCount) return;
    busy.current = true;
    setExporting(true);
    setError(false);
    try {
      const csv = await createSessionsCsv(sessions, translate, session => extractSubcollection(session, "blocks"));
      if (active.current && csv) downloadSessionsCsv(csv);
    } catch {
      if (active.current) setError(true);
    } finally {
      busy.current = false;
      if (active.current) setExporting(false);
    }
  }

  return <>
    <Button onClick={exportCsv} disabled={disabled || exporting || !supportedCount}
      startIcon={exporting ? <CircularProgress size={16} /> : <DownloadIcon />}>
      {translate(exporting ? "[session_exporting_csv]" : "[session_export_csv]")}
    </Button>
    {!disabled && supportedCount < sessions.length &&
      <Alert severity="info" sx={{ textAlign: "left" }}>{translate("[session_csv_unsupported]")}</Alert>}
    {error && <Alert severity="error" sx={{ textAlign: "left" }}>{translate("[session_csv_error]")}</Alert>}
  </>;
}
